<template>
    <div class="card mb-3 minimum-profile-form">
        <div class="card-header">
            <div class="d-flex flex-column flex-md-row align-items-start gap-2 gap-md-4">
                <div class="flex-shrink-0">
                    <strong>Complete your profile</strong>
                    <div class="mt-2">
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-primary"
                            :disabled="!isMinimumComplete"
                            :title="isMinimumComplete ? null : 'Fill in your name and select a credit type (or No credit) to confirm'"
                            @click="onConfirm"
                        >
                            Confirm
                        </button>
                    </div>
                </div>
                <ul class="mpf-muted small mb-0 ps-3 mpf-intro-list">
                    <li>The minimum we need to issue certificates and submit attendance for credit</li>
                    <li>Saves automatically as you type</li>
                    <li>Click <strong>Confirm</strong> when your information is complete</li>
                    <li>Update anything else on your <router-link to="/profile" class="mpf-inline-link">profile page</router-link></li>
                </ul>
            </div>
        </div>
        <div class="card-body">
            <form v-if="null != attendee" @submit.prevent>
            <fieldset class="border-0 p-0 mb-0">
                <legend class="form-label fw-bold mb-2 fs-9">Your name</legend>
                <div class="row g-2">
                    <div class="col-6 col-md-2">
                        <label class="form-label small mb-1 d-flex align-items-center gap-1" for="mpf-display-name">
                            <span>Display name</span>
                            <button type="button" class="mpf-info-icon"
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover focus click"
                                    data-bs-placement="top"
                                    :title="tooltips.displayName"
                                    aria-label="About: Display name">
                                <vue-feather type="info" size="0.85rem"></vue-feather>
                            </button>
                        </label>
                        <input
                            id="mpf-display-name"
                            type="text"
                            class="form-control form-control-sm"
                            :value="displayName"
                            @input="onDisplayNameInput"
                        />
                    </div>
                    <div class="col-6 col-md-2">
                        <label class="form-label small mb-1 d-flex align-items-center gap-1" for="mpf-given-name">
                            <span>First name</span>
                            <button type="button" class="mpf-info-icon"
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover focus click"
                                    data-bs-placement="top"
                                    :title="tooltips.givenName"
                                    aria-label="About: First name">
                                <vue-feather type="info" size="0.85rem"></vue-feather>
                            </button>
                        </label>
                        <input
                            id="mpf-given-name"
                            type="text"
                            class="form-control form-control-sm"
                            :value="givenName"
                            @input="onGivenNameInput"
                        />
                    </div>
                    <div class="col-6 col-md-2">
                        <label class="form-label small mb-1 d-flex align-items-center gap-1" for="mpf-family-name">
                            <span>Last name</span>
                            <button type="button" class="mpf-info-icon"
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover focus click"
                                    data-bs-placement="top"
                                    :title="tooltips.familyName"
                                    aria-label="About: Last name">
                                <vue-feather type="info" size="0.85rem"></vue-feather>
                            </button>
                        </label>
                        <input
                            id="mpf-family-name"
                            type="text"
                            class="form-control form-control-sm"
                            :value="familyName"
                            @input="onFamilyNameInput"
                        />
                    </div>
                </div>
            </fieldset>

            <hr class="mpf-section-divider" />

            <fieldset class="border-0 p-0 mb-0 mpf-address">
                <legend class="form-label mb-2 fs-9 d-flex flex-wrap align-items-center gap-2">
                    <span class="fw-bold">Mailing address</span>
                    <span id="mpf-address-status" :class="addressStatusClass"
                        class="fw-normal d-inline-flex align-items-center gap-1" aria-live="polite">
                        <vue-feather v-if="addressStatusIcon" :type="addressStatusIcon" size="0.85rem"></vue-feather>
                        <span>{{ addressStatusMessage }}</span>
                    </span>
                </legend>
                <div v-if="uspsSuggestion" class="border border-primary rounded py-2 px-3 small mb-2 d-inline-flex flex-wrap align-items-center gap-2 mw-100">
                    <span>USPS suggests: <strong>{{ uspsSuggestionText }}</strong></span>
                    <button type="button" class="btn btn-sm btn-outline-primary" @click="applyUspsSuggestion">Use this</button>
                </div>
                <div class="row g-2">
                    <div class="col-6 col-md-3">
                        <label class="form-label small mb-1" for="mpf-country">Country</label>
                        <CountrySelector elementId="mpf-country" cssClass="form-select form-select-sm"
                            :boundObject="address" boundProperty="country" :changeHandler="onCountryChange" />
                    </div>
                    <template v-if="isUsAddress">
                        <div class="w-100"></div>
                        <div class="col-12 col-md-6">
                            <label class="form-label small mb-1" for="mpf-address1">Street address</label>
                            <input id="mpf-address1" type="text" class="form-control form-control-sm"
                                placeholder="Street address or PO box"
                                :value="address.address1" @input="onAddressInput('address1', $event)" />
                        </div>
                        <div class="col-6 col-md-3">
                            <label class="form-label small mb-1" for="mpf-address2">Apt, suite, etc.</label>
                            <input id="mpf-address2" type="text" class="form-control form-control-sm"
                                :value="address.address2" @input="onAddressInput('address2', $event)" />
                        </div>
                        <div class="w-100"></div>
                        <div class="col-6 col-md-4">
                            <label class="form-label small mb-1" for="mpf-city">City</label>
                            <input id="mpf-city" type="text" class="form-control form-control-sm"
                                :value="address.city" @input="onAddressInput('city', $event)" />
                        </div>
                        <div class="col-6 col-md-3">
                            <label class="form-label small mb-1" for="mpf-state">State</label>
                            <StateSelector elementId="mpf-state" cssClass="form-select form-select-sm"
                                :boundObject="address" boundProperty="state" :changeHandler="onStructuredAddressChange" />
                        </div>
                        <div class="col-6 col-md-2">
                            <label class="form-label small mb-1" for="mpf-postal">ZIP code</label>
                            <input id="mpf-postal" type="text" class="form-control form-control-sm mpf-postal-input"
                                :value="address.postal_code" @input="onAddressInput('postal_code', $event)" />
                        </div>
                    </template>
                </div>
            </fieldset>

            <hr class="mpf-section-divider" />

            <fieldset class="border-0 p-0 mb-0" aria-labelledby="mpf-credit-title">
                <div class="d-flex flex-column flex-md-row align-items-start gap-2 gap-md-4">
                    <div class="flex-shrink-0">
                        <div id="mpf-credit-title" class="form-label fw-bold mb-2 fs-9">Eligible credit types</div>
                        <div class="form-check">
                            <input
                                id="mpf-no-credit"
                                type="checkbox"
                                class="form-check-input"
                                :checked="noCeCredit"
                                @change="onNoCreditToggle($event)"
                            />
                            <label class="form-check-label small cursor-pointer" for="mpf-no-credit">
                                <strong>No credit</strong>
                            </label>
                        </div>
                    </div>
                    <ul class="mpf-muted small mb-0 ps-3 mpf-intro-list flex-grow-1">
                        <li><strong>No credit:</strong> you still receive a certificate of attendance.</li>
                        <li><strong>Earning credit:</strong> select the credit type(s) and add the required license number(s) so we can submit your attendance.</li>
                    </ul>
                </div>
                <hr class="mpf-credit-divider" />
                <div class="row g-1">
                    <div
                        v-for="creditType in ceCreditTypes"
                        :key="creditType.id"
                        class="col-12 col-md-6"
                    >
                        <div class="form-check">
                            <input
                                :id="'mpf-credit-' + creditType.id"
                                type="checkbox"
                                class="form-check-input"
                                :checked="isCreditChecked(creditType.id)"
                                :disabled="noCeCredit"
                                @change="onCreditToggle(creditType, $event)"
                            />
                            <label class="form-check-label small" :for="'mpf-credit-' + creditType.id">
                                {{ creditType.name }}
                            </label>
                        </div>
                        <div
                            v-if="creditType.professional_license_type && isCreditChecked(creditType.id)"
                            class="ms-4 mb-1"
                        >
                            <input
                                type="text"
                                class="form-control form-control-sm mpf-license-input"
                                :placeholder="creditType.professional_license_type.placeholder || 'License number'"
                                :value="licenseValues[creditType.professional_license_type.id] || ''"
                                :disabled="noCeCredit"
                                :aria-invalid="!!licenseErrors[creditType.professional_license_type.id]"
                                :aria-describedby="licenseErrors[creditType.professional_license_type.id]
                                    ? ('mpf-license-error-' + creditType.professional_license_type.id)
                                    : null"
                                @input="onLicenseInput(creditType, $event)"
                            />
                            <span
                                v-if="licenseErrors[creditType.professional_license_type.id]"
                                :id="'mpf-license-error-' + creditType.professional_license_type.id"
                                class="small mpf-error"
                            >Enter your complete license number</span>
                        </div>
                    </div>
                </div>
            </fieldset>

            <div class="mt-3">
                <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    :disabled="!isMinimumComplete"
                    :title="isMinimumComplete ? null : 'Fill in your name and select a credit type (or No credit) to confirm'"
                    @click="onConfirm"
                >
                    Confirm
                </button>
            </div>
            <div v-if="saveStatus" class="small mpf-muted mt-2" aria-live="polite">{{ saveStatus }}</div>
            </form>
        </div>
    </div>
</template>

<script>
import * as bootstrap from 'bootstrap'
import VueFeather from 'vue-feather'
import CountrySelector from '@/components/CountrySelector'
import StateSelector from '@/components/StateSelector'
import {callApi} from '@/shared/ApiHelper'
import {isMinimumProfileComplete} from '@/shared/MinimumProfile'
import {useApiStore} from '@/store/api'
import {reloadAttendee} from '@/shared/susiHelper'

const TEXT_DEBOUNCE_MS = 500

export default {
    name: 'MinimumProfileForm',

    components: {
        CountrySelector,
        StateSelector,
        VueFeather,
    },

    emits: ['saved'],

    setup() {
        const apiStore = useApiStore()
        return {apiStore}
    },

    data() {
        return {
            attendee: null,
            ceCreditTypes: [],
            displayName: '',
            givenName: '',
            familyName: '',
            noCeCredit: false,
            selectedCreditTypes: {},
            licenseValues: {},
            licenseErrors: {},
            saveTimers: {},
            saveStatus: '',
            pendingSaveCount: 0,
            confirming: false,
            address: {
                id: null,
                name: null,
                address1: '',
                address2: '',
                city: '',
                state: 'PA',
                postal_code: '',
                country: 'US',
                usps_validated: false,
            },
            uspsSuggestion: null,
            // '' | 'checking' | 'validated' | 'unverified'
            uspsStatus: '',
            // The last complete address we ran through USPS, plus its outcome,
            // so we can reuse the result instead of re-calling the API for an
            // unchanged address.
            lastCheck: {signature: null, status: '', validated: false, suggestion: null},
            tooltips: {
                displayName: 'Name as it should appear on badges, attendee lists, session titles, etc.',
                givenName: 'Used on your certificates of attendance and submitted to credit-issuing organizations.',
                familyName: 'Used on your certificates of attendance and submitted to credit-issuing organizations.',
            },
        }
    },

    mounted() {
        this.initTooltips()
    },

    beforeUnmount() {
        for (const key in this.saveTimers) {
            clearTimeout(this.saveTimers[key])
        }
        this.disposeTooltips()
    },

    computed: {
        isMinimumComplete() {
            return isMinimumProfileComplete(this.apiStore.currentAttendee)
        },
        isUsAddress() {
            return 'US' === this.address.country
        },
        uspsSuggestionText() {
            const suggestion = this.uspsSuggestion
            if (null == suggestion) {
                return ''
            }
            const line = [suggestion.address1, suggestion.address2].filter(Boolean).join(', ')
            const cityStateZip = [
                suggestion.city,
                [suggestion.state, suggestion.postal_code].filter(Boolean).join(' '),
            ].filter(Boolean).join(', ')
            return [line, cityStateZip].filter(Boolean).join(', ')
        },
        // The line shown right under the "Mailing address" label. It starts as
        // an informational hint and is replaced by the USPS check outcome.
        addressStatusMessage() {
            switch (this.uspsStatus) {
            case 'checking':
                return 'Checking this address with USPS…'
            case 'validated':
                return 'This address is validated by USPS.'
            case 'unverified':
                return 'This address could not be validated by USPS. You can still continue.'
            case 'suggestion':
                return 'USPS suggests a standardized version of this address.'
            }
            if (!this.isUsAddress) {
                return 'Only your country is recorded for addresses outside the US.'
            }
            return 'US addresses are USPS verified.'
        },
        addressStatusClass() {
            switch (this.uspsStatus) {
            case 'validated':
                return 'mpf-status-success'
            case 'unverified':
                return 'mpf-status-warning'
            case 'suggestion':
                return 'mpf-status-info'
            default:
                return 'mpf-muted'
            }
        },
        addressStatusIcon() {
            switch (this.uspsStatus) {
            case 'validated':
                return 'check-circle'
            case 'unverified':
                return 'alert-triangle'
            case 'suggestion':
                return 'info'
            default:
                return null
            }
        },
    },

    async created() {
        this.attendee = this.apiStore.currentAttendee
        if (null == this.attendee) {
            return
        }

        this.seedFromAttendee(this.attendee)

        const apiResponse = await callApi(this.apiStore.buildUrl('/events/cecredittypes/'))
        if (Array.isArray(apiResponse.data)) {
            this.ceCreditTypes = apiResponse.data
        }
    },

    methods: {
        seedFromAttendee(attendee) {
            this.displayName = attendee.display_name || ''
            this.givenName = attendee.given_name || ''
            this.familyName = attendee.family_name || ''
            this.noCeCredit = !!attendee.no_ce_credit
            const selected = {}
            if (attendee.eligible_ce_credit_types) {
                for (const credit of attendee.eligible_ce_credit_types) {
                    selected[credit.id] = true
                }
            }
            this.selectedCreditTypes = selected
            const licenses = {}
            if (attendee.professional_licenses) {
                for (const license of attendee.professional_licenses) {
                    licenses[license.id] = license.license_number
                }
            }
            this.licenseValues = licenses
            this.licenseErrors = {}

            const existingAddress = attendee.registration_address
            if (null != existingAddress) {
                this.address = {
                    id: existingAddress.id || null,
                    name: existingAddress.name || null,
                    address1: existingAddress.address1 || '',
                    address2: existingAddress.address2 || '',
                    city: existingAddress.city || '',
                    state: existingAddress.state || 'PA',
                    postal_code: existingAddress.postal_code || '',
                    country: existingAddress.country || 'US',
                    usps_validated: !!existingAddress.usps_validated,
                }
                // Reflect a previously USPS-validated address on load, and
                // record its signature so we don't re-call USPS unless the
                // user actually changes it.
                if (this.address.usps_validated && 'US' === this.address.country
                        && this.hasRequiredAddressFields()) {
                    this.uspsStatus = 'validated'
                    this.lastCheck = {
                        signature: this.addressSignature(),
                        status: 'validated',
                        validated: true,
                        suggestion: null,
                    }
                }
            }
        },

        isCreditChecked(creditTypeId) {
            return !!this.selectedCreditTypes[creditTypeId]
        },

        onDisplayNameInput(event) {
            this.displayName = event.target.value
            this.scheduleTextSave('display_name', () => ({display_name: this.displayName.trim()}))
        },

        onGivenNameInput(event) {
            this.givenName = event.target.value
            this.scheduleTextSave('given_name', () => ({given_name: this.givenName.trim()}))
        },

        onFamilyNameInput(event) {
            this.familyName = event.target.value
            this.scheduleTextSave('family_name', () => ({family_name: this.familyName.trim()}))
        },

        scheduleTextSave(timerKey, payloadBuilder) {
            if (this.saveTimers[timerKey]) {
                clearTimeout(this.saveTimers[timerKey])
            }
            this.saveTimers[timerKey] = setTimeout(() => {
                delete this.saveTimers[timerKey]
                this.patchAttendee(payloadBuilder())
            }, TEXT_DEBOUNCE_MS)
        },

        async onNoCreditToggle(event) {
            const checked = event.target.checked
            if (checked) {
                const hasSelectedCredits = 0 < Object.keys(this.selectedCreditTypes).length
                if (hasSelectedCredits) {
                    const confirmed = window.confirm(
                        "Selecting 'No credit' will remove all other credit types and license numbers. Continue?"
                    )
                    if (!confirmed) {
                        event.target.checked = false
                        return
                    }
                }
                this.selectedCreditTypes = {}
                this.licenseValues = {}
                this.licenseErrors = {}
                this.noCeCredit = true
                await this.patchAttendee({
                    no_ce_credit: true,
                    eligible_ce_credit_types: [],
                    professional_licenses: [],
                })
            } else {
                this.noCeCredit = false
                await this.patchAttendee({no_ce_credit: false})
            }
        },

        async onCreditToggle(creditType, event) {
            const checked = event.target.checked
            if (checked) {
                this.noCeCredit = false
                this.selectedCreditTypes = {...this.selectedCreditTypes, [creditType.id]: true}
            } else {
                const next = {...this.selectedCreditTypes}
                delete next[creditType.id]
                this.selectedCreditTypes = next
                if (creditType.professional_license_type) {
                    const licenseTypeId = creditType.professional_license_type.id
                    const nextLicenses = {...this.licenseValues}
                    delete nextLicenses[licenseTypeId]
                    this.licenseValues = nextLicenses
                    this.licenseErrors = {...this.licenseErrors, [licenseTypeId]: false}
                }
            }
            await this.saveCreditTypes()
        },

        onLicenseInput(creditType, event) {
            const licenseTypeId = creditType.professional_license_type.id
            const value = event.target.value
            this.licenseValues = {...this.licenseValues, [licenseTypeId]: value}
            this.licenseErrors = {...this.licenseErrors, [licenseTypeId]: false}

            if (this.saveTimers[licenseTypeId]) {
                clearTimeout(this.saveTimers[licenseTypeId])
            }
            this.saveTimers[licenseTypeId] = setTimeout(() => {
                delete this.saveTimers[licenseTypeId]
                if (!value) {
                    this.saveCreditTypes()
                    return
                }
                const expression = creditType.professional_license_type.validation_expression
                if (expression) {
                    const regex = new RegExp(expression)
                    if (regex.test(value)) {
                        this.licenseErrors = {...this.licenseErrors, [licenseTypeId]: false}
                        this.saveCreditTypes()
                    } else {
                        this.licenseErrors = {...this.licenseErrors, [licenseTypeId]: true}
                    }
                } else {
                    this.saveCreditTypes()
                }
            }, TEXT_DEBOUNCE_MS)
        },

        // A credit type that needs a license number is never saved without it
        // (the server refuses the pair). While a ticked one has no number yet,
        // hold the credit save: ticking the box would otherwise fail before the
        // number field has even appeared (#1042).
        creditTypeAwaitsNumber() {
            return this.ceCreditTypes.some(creditType => this.selectedCreditTypes[creditType.id]
                && creditType.professional_license_type
                && !(this.licenseValues[creditType.professional_license_type.id] || '').trim())
        },

        saveCreditTypes() {
            if (this.creditTypeAwaitsNumber()) {
                return
            }
            return this.patchAttendee(this.buildCreditPayload())
        },

        buildCreditPayload() {
            const eligibleCreditTypes = this.ceCreditTypes.filter(
                creditType => this.selectedCreditTypes[creditType.id]
            )
            const professionalLicenses = []
            for (const licenseTypeId in this.licenseValues) {
                const value = (this.licenseValues[licenseTypeId] || '').trim()
                if (value) {
                    professionalLicenses.push({
                        id: parseInt(licenseTypeId, 10),
                        license_number: value,
                    })
                }
            }
            return {
                no_ce_credit: this.noCeCredit,
                eligible_ce_credit_types: eligibleCreditTypes,
                professional_licenses: professionalLicenses,
            }
        },

        onCountryChange() {
            // The CountrySelector has already written the new value to
            // this.address.country via its v-model binding.
            this.onAddressEdited()
        },

        onStructuredAddressChange() {
            // The StateSelector has already written the new value to
            // this.address.state via its v-model binding.
            this.onAddressEdited()
        },

        onAddressInput(field, event) {
            this.address[field] = event.target.value
            this.onAddressEdited()
        },

        onAddressEdited() {
            // The edited address invalidates any prior USPS suggestion. Show a
            // pending "checking" status once the address is complete so the
            // user knows a verification is coming; the debounced save decides
            // whether an API call is actually needed.
            this.uspsSuggestion = null
            this.uspsStatus = (this.isUsAddress && this.hasRequiredAddressFields()) ? 'checking' : ''
            this.scheduleAddressSave()
        },

        scheduleAddressSave() {
            if (this.saveTimers.address) {
                clearTimeout(this.saveTimers.address)
            }
            this.saveTimers.address = setTimeout(() => {
                delete this.saveTimers.address
                this.validateAndSaveAddress()
            }, TEXT_DEBOUNCE_MS)
        },

        hasRequiredAddressFields() {
            return ['address1', 'city', 'state', 'postal_code', 'country'].every((field) => {
                const value = this.address[field]
                return 'string' === typeof value && '' !== value.trim()
            })
        },

        addressSignature() {
            return ['address1', 'address2', 'city', 'state', 'postal_code', 'country']
                .map((field) => (this.address[field] || '').trim().toUpperCase())
                .join('|')
        },

        async validateAndSaveAddress() {
            // Non-US: only the country is recorded. Blank the US-format fields
            // (so a previously-entered US address or the default 'PA' state is
            // not persisted) and save just the country.
            if (!this.isUsAddress) {
                this.address.address1 = ''
                this.address.address2 = ''
                this.address.city = ''
                this.address.state = ''
                this.address.postal_code = ''
                this.address.usps_validated = false
                this.uspsSuggestion = null
                this.uspsStatus = ''
                await this.saveAddress()
                return
            }

            // Don't create an empty Address row just because a field was
            // focused. But once an address exists, always save — including
            // edits that clear fields — so deletions persist.
            const hasEnteredData = ['address1', 'address2', 'city', 'postal_code']
                .some((field) => '' !== (this.address[field] || '').trim())
            if (null == this.address.id && !hasEnteredData) {
                return
            }

            // Autosave whatever is currently entered, complete or not. An
            // incomplete address can't be USPS-validated and won't satisfy the
            // completeness rule, but the edit still has to persist.
            if (!this.hasRequiredAddressFields()) {
                this.address.usps_validated = false
                this.uspsSuggestion = null
                this.uspsStatus = ''
                await this.saveAddress()
                return
            }

            const signature = this.addressSignature()

            // Smart check: only hit the USPS API when this complete address
            // differs from the one we last checked. An unchanged address reuses
            // the stored outcome with no API round-trip.
            if (signature === this.lastCheck.signature) {
                this.address.usps_validated = this.lastCheck.validated
                this.uspsSuggestion = this.lastCheck.suggestion
                this.uspsStatus = this.lastCheck.status
                await this.saveAddress()
                return
            }

            this.uspsStatus = 'checking'
            const result = await this.requestUspsValidation()
            if (result && result.validated && result.standardized) {
                if (this.addressMatchesStandardized(result.standardized)) {
                    // Same address; adopt USPS's standardized spelling, in
                    // proper case (not USPS's all-caps).
                    this.applyStandardizedFields(result.standardized)
                    this.address.usps_validated = true
                    this.uspsSuggestion = null
                    this.uspsStatus = 'validated'
                } else {
                    // Deliverable, but USPS standardizes it differently. Save
                    // what they typed and offer the standardized form, proper-cased.
                    this.address.usps_validated = false
                    this.uspsSuggestion = {
                        ...result.standardized,
                        address1: this.properCase(result.standardized.address1 || ''),
                        address2: this.properCase(result.standardized.address2 || ''),
                        city: this.properCase(result.standardized.city || ''),
                    }
                    this.uspsStatus = 'suggestion'
                }
            } else {
                // USPS is a soft check: a no-match is saved as entered.
                this.address.usps_validated = false
                this.uspsSuggestion = null
                this.uspsStatus = 'unverified'
            }
            // Recompute the signature: adopting the standardized form above
            // changes the address fields, and lastCheck must reflect that so an
            // unchanged address isn't re-validated.
            this.lastCheck = {
                signature: this.addressSignature(),
                status: this.uspsStatus,
                validated: this.address.usps_validated,
                suggestion: this.uspsSuggestion,
            }
            await this.saveAddress()
        },

        async requestUspsValidation() {
            try {
                const response = await callApi(
                    this.apiStore.buildUrl('/events/addresses/validate/'),
                    'POST',
                    {
                        address1: this.address.address1,
                        address2: this.address.address2,
                        city: this.address.city,
                        state: this.address.state,
                        postal_code: this.address.postal_code,
                        country: this.address.country,
                    },
                )
                if (response.status >= 200 && response.status <= 209) {
                    return response.data
                }
            } catch (error) {
                console.error('USPS validation error:', error)
            }
            return null
        },

        addressMatchesStandardized(standardized) {
            return ['address1', 'address2', 'city', 'state', 'postal_code'].every((field) => {
                const entered = (this.address[field] || '').trim().toUpperCase()
                const suggested = (standardized[field] || '').trim().toUpperCase()
                return entered === suggested
            })
        },

        properCase(value) {
            if (null == value) {
                return value
            }
            // We don't ship mail, so USPS's all-caps isn't needed; present
            // addresses in proper case (also fixes all-lowercase entry). This
            // capitalizes the first letter of each word — not a full postal
            // normalizer (won't special-case McNames, directionals, etc.).
            return value.toLowerCase().replace(/\b[a-z]/g, (char) => char.toUpperCase())
        },

        applyStandardizedFields(standardized) {
            this.address.address1 = this.properCase(standardized.address1 || '')
            this.address.address2 = this.properCase(standardized.address2 || '')
            this.address.city = this.properCase(standardized.city || '')
            this.address.state = standardized.state || this.address.state
            this.address.postal_code = standardized.postal_code || ''
        },

        applyUspsSuggestion() {
            const suggestion = this.uspsSuggestion
            if (null == suggestion) {
                return
            }
            this.applyStandardizedFields(suggestion)
            this.address.usps_validated = true
            this.uspsSuggestion = null
            this.uspsStatus = 'validated'
            // The address now equals USPS's standardized form, so record it as
            // the last checked address to avoid re-validating the same values.
            this.lastCheck = {
                signature: this.addressSignature(),
                status: 'validated',
                validated: true,
                suggestion: null,
            }
            this.saveAddress()
        },

        async saveAddress() {
            // Normalize the free-text fields to proper case before persisting,
            // so whatever the attendee typed (all-caps, all-lowercase) and
            // whatever USPS returned ends up consistently capitalized.
            this.address.address1 = this.properCase(this.address.address1 || '')
            this.address.address2 = this.properCase(this.address.address2 || '')
            this.address.city = this.properCase(this.address.city || '')
            const method = this.address.id ? 'PUT' : 'POST'
            const payload = {
                name: this.address.name || this.displayName || this.apiStore.displayName || 'Mailing address',
                address1: this.address.address1,
                address2: this.address.address2 || '',
                city: this.address.city,
                state: this.address.state,
                postal_code: this.address.postal_code,
                country: this.address.country,
                usps_validated: this.address.usps_validated,
            }
            if (this.address.id) {
                payload.id = this.address.id
            }
            const url = this.apiStore.buildUrl('/events/addresses/')
                + ('PUT' === method ? this.address.id + '/' : '')
            this.pendingSaveCount++
            this.saveStatus = 'Saving…'
            try {
                const response = await callApi(url, method, payload)
                if (response.status >= 200 && response.status <= 209) {
                    if (response.data && response.data.id) {
                        // First save creates the Address and links it to the
                        // attendee; capture the id/name so later edits PUT.
                        this.address.id = response.data.id
                        this.address.name = response.data.name
                    }
                    // Mirror patchAttendee: only the last in-flight save reloads
                    // so concurrent saves can't leave the store stale.
                    if (1 === this.pendingSaveCount) {
                        await reloadAttendee()
                        this.attendee = this.apiStore.currentAttendee
                    }
                    this.saveStatus = 'Saved'
                    const isComplete = isMinimumProfileComplete(this.apiStore.currentAttendee)
                    this.$emit('saved', {isComplete})
                } else {
                    this.saveStatus = 'Save failed'
                }
            } catch (error) {
                console.error('MinimumProfileForm address save error:', error)
                this.saveStatus = 'Save failed'
            } finally {
                this.pendingSaveCount--
            }
        },

        async patchAttendee(payload) {
            const attendeeId = this.apiStore.currentAttendee && this.apiStore.currentAttendee.id
            if (null == attendeeId) {
                return
            }
            this.pendingSaveCount++
            this.saveStatus = 'Saving…'
            try {
                const response = await callApi(
                    this.apiStore.buildUrl('/events/attendees/' + attendeeId + '/'),
                    'PATCH',
                    {id: attendeeId, ...payload},
                )
                if (response.status >= 200 && response.status <= 209) {
                    // The Django update endpoint returns {status, message} (not the
                    // serialized attendee), so we re-fetch via reloadAttendee so the
                    // store reflects the freshly-saved state with full structure.
                    // Only the last in-flight save triggers the reload so concurrent
                    // PATCHes can't race their GETs and leave the store reflecting
                    // a stale earlier save. The check is `1 ===` because the decrement
                    // happens in the finally block below; when we're the only save in
                    // flight the count is still 1 here.
                    if (1 === this.pendingSaveCount) {
                        await reloadAttendee()
                        this.attendee = this.apiStore.currentAttendee
                    }
                    this.saveStatus = 'Saved'
                    const isComplete = isMinimumProfileComplete(this.apiStore.currentAttendee)
                    this.$emit('saved', {isComplete})
                } else {
                    this.saveStatus = 'Save failed'
                }
            } catch (error) {
                console.error('MinimumProfileForm save error:', error)
                this.saveStatus = 'Save failed'
            } finally {
                // Always exactly one decrement per save, no matter where the error
                // surfaces (callApi or reloadAttendee). Previously decrementing in
                // both try and catch could drive the counter negative if
                // reloadAttendee threw, permanently breaking the "last save reloads"
                // gate for the rest of the component's lifetime.
                this.pendingSaveCount--
            }
        },

        async onConfirm() {
            const attendeeId = this.apiStore.currentAttendee && this.apiStore.currentAttendee.id
            if (null == attendeeId) {
                return
            }
            if (this.confirming) {
                return
            }
            this.confirming = true
            try {
                const response = await callApi(
                    this.apiStore.buildUrl('/events/attendees/' + attendeeId + '/confirm_minimum_profile/'),
                    'POST',
                    {},
                )
                if (response && response.status >= 200 && response.status <= 209) {
                    // Reload so apiStore.currentAttendee.last_minimum_profile_confirmation
                    // reflects the server-stamped timestamp; the gate's isHidden recomputes
                    // and hides the form on its own.
                    await reloadAttendee()
                    this.attendee = this.apiStore.currentAttendee
                } else {
                    console.error('MinimumProfileForm confirm failed:', response)
                    this.saveStatus = 'Confirm failed'
                }
            } catch (error) {
                console.error('MinimumProfileForm confirm error:', error)
                this.saveStatus = 'Confirm failed'
            } finally {
                this.confirming = false
            }
        },

        initTooltips() {
            this.disposeTooltips()
            this._tooltipInstances = []
            this.$el.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
                this._tooltipInstances.push(new bootstrap.Tooltip(el))
            })
        },

        disposeTooltips() {
            if (this._tooltipInstances) {
                this._tooltipInstances.forEach(t => t.dispose())
                this._tooltipInstances = []
            }
        },
    },
}
</script>

<style scoped>
.minimum-profile-form .mpf-credit-divider {
    margin: 0 0 0.75rem;
    border-top: 2px solid #dee2e6;
    opacity: 1;
}

.minimum-profile-form .mpf-section-divider {
    margin: 1rem 0;
    border-top: 2px solid #dee2e6;
    opacity: 1;
}

.minimum-profile-form .mpf-license-input {
    max-width: 30ch;
}

.minimum-profile-form .mpf-postal-input {
    max-width: 12ch;
}

/* Muted helper text, darkened to #525b75 for a 6.75:1 contrast on the white
   card (WCAG AA; the theme's .text-muted #8a94ad is only 3.03:1). */
.minimum-profile-form .mpf-muted {
    color: #525b75;
}

/* Compact intro list, kept beside the title/Confirm on wider screens and
   stacked below them on small screens (flex-column flex-md-row in the header). */
.minimum-profile-form .mpf-intro-list {
    line-height: 1.35;
}

.minimum-profile-form .mpf-intro-list li {
    margin-bottom: 0.15rem;
}

/* a11y (WCAG 1.4.1): underline the inline body-copy "profile page" link so it
   is distinguishable from surrounding text without relying on color. */
.minimum-profile-form .mpf-inline-link {
    text-decoration: underline;
}

/* Status / error colors darkened to clear WCAG AA (>=4.5:1) on the white card.
   The theme's text-success (#25b003, 2.88:1) and text-danger (#ed2000, 4.37:1)
   fall short, and text-warning-emphasis / text-info-emphasis don't exist in
   this theme version. */
.minimum-profile-form .mpf-status-success {
    color: #177a00; /* 5.5:1 */
}

.minimum-profile-form .mpf-status-warning {
    color: #9c5400; /* 5.7:1 */
}

.minimum-profile-form .mpf-status-info {
    color: #2160c4; /* 5.9:1 */
}

.minimum-profile-form .mpf-error {
    color: #c41a00; /* 6.0:1 */
}

.minimum-profile-form .mpf-info-icon {
    background: none;
    border: none;
    /* a11y (WCAG 2.5.8): pad the ~13.6px icon out to a >=24x24px hit target. */
    padding: 4px;
    min-width: 24px;
    min-height: 24px;
    box-sizing: border-box;
    margin: 0;
    color: #2c7be5;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    border-radius: 50%;
}

.minimum-profile-form .mpf-info-icon:hover {
    color: #1a5cb8;
}

.minimum-profile-form .mpf-info-icon:focus-visible {
    outline: 2px solid #2c7be5;
    outline-offset: 2px;
}
</style>
